import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { ToastProvider } from "./components/ui/Toast";
import { NavigationProvider } from './contexts/NavigationContext';
import { ProtectedRoute } from './components/ProtectedRoute';

import Layout from './components/Layout';
import LoginForm from './LoginForm';

import OAuthCallback from './components/OAuthCallback';

import ToolsList from './tools/ToolsList';
import AddToolPage from './tools/AddToolPage';
import EditToolPage from './tools/EditToolPage';
import TestToolPage from './tools/TestToolPage';
import ViewToolPage from './tools/ViewToolPage';

import AgentsListModern from './agents/AgentsListModern';
import AddAgentPage from './agents/AddAgentPage';
import EditAgentPage from './agents/EditAgentPage';
import ViewAgentPage from './agents/ViewAgentPage';

import PlaybooksList from './playbooks/PlaybooksList';
import InteractivePlaybooksList from './playbooks/InteractivePlaybooksList';
import AddPlaybookPage from './playbooks/AddPlaybookPage';
import AddDialoguePage from './playbooks/AddDialoguePage';
import EditPlaybookPage from './playbooks/EditPlaybookPage';
import ViewPlaybookPage from './playbooks/ViewPlaybookPage';

import EditPromptPage from './prompts/EditPromptPage';

import { AuthProvider } from './contexts/AuthContext';

import CoreItemsPage from './admin/CoreItemsPage';
import AdminLanding from './admin/AdminLandingPage';
import UserManagementPage from './admin/UserManagementPage';

import AdaptersList from './adapters/AdaptersList';
import AddAdapterPage from './adapters/AddAdapterPage';
import EditAdapterPage from './adapters/EditAdapterPage';
import TestAdapterPage from './adapters/TestAdapterPage';

import AllExecutionsPage from './executions/AllExecutionsPage';
import ExecutionDetailsPage from './executions/ExecutionDetailsPage';
        
import NotFound from './components/NotFound';
import UnauthorizedPage from './components/Unauthorized';
import RootRedirect from './components/RootRedirect';
import StudioRedirect from './components/StudioRedirect';
import SessionExpiryHandler from './components/SessionExpiryHandler';
import ChatWindow from './chatwindow/page';

// Wrap the entity pages with entity-specific protection
interface ProtectedEntityPageProps {
  children: React.ReactNode;
  requireEdit?: boolean;
}

// Wrap the entity pages with entity-specific protection
const ProtectedToolPage: React.FC<ProtectedEntityPageProps> = ({ 
  children, 
  requireEdit = false 
}) => (
  <ProtectedRoute entityType="tool">
    {children}
  </ProtectedRoute>
);

const ProtectedAgentPage: React.FC<ProtectedEntityPageProps> = ({ 
  children, 
  requireEdit = false 
}) => (
  <ProtectedRoute entityType="agent">
    {children}
  </ProtectedRoute>
);

const ProtectedPlaybookPage: React.FC<ProtectedEntityPageProps> = ({ 
  children, 
  requireEdit = false 
}) => (
  <ProtectedRoute entityType="playbook">
    {children}
  </ProtectedRoute>
);

// Wrap the Layout component with NavigationProvider and ProtectedRoute
const ProtectedLayout = () => (
  <ProtectedRoute>
    <NavigationProvider>
      <Layout />
    </NavigationProvider>
  </ProtectedRoute>
);

const AdminProtectedLayout = () => (
  <ProtectedRoute requireAdmin>
    <NavigationProvider>
      <Layout />
    </NavigationProvider>
  </ProtectedRoute>
);

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootRedirect />
  },
  {
    path: '/studio',
    element: <StudioRedirect />
  },
  {
    path: '/login',
    element: <LoginForm />
  },
  {
    path: '/oauth/callback',
    element: <OAuthCallback />
  },
  {
    path: '/studio/unauthorized',
    element: <UnauthorizedPage />
  },
  {
    element: <ProtectedLayout />,
    children: [
      // Tools Routes
      {
        path: '/studio/tools',
        element: <ToolsList />
      },
      {
        path: '/studio/tools/add',
        element: <AddToolPage />
      },
      {
        path: '/studio/tools/view/:id',
        element: (
          <ProtectedToolPage>
            <ViewToolPage />
          </ProtectedToolPage>
        )
      },
      {
        path: '/studio/tools/edit/:id',
        element: (
          <ProtectedToolPage>
            <EditToolPage />
          </ProtectedToolPage>
        )
      },
      {
        path: '/studio/tools/test/:id',
        element: (
          <ProtectedToolPage>
            <TestToolPage />
          </ProtectedToolPage>
        )
      },

      // Chat Routes
      {
        path: '/studio/chat',
        element: <ChatWindow />
      },

      // Agents Routes
      {
        path: '/studio/agents',
        element: <AgentsListModern />
      },
      {
        path: '/studio/agents/add',
        element: <AddAgentPage />
      },
      {
        path: '/studio/agents/view/:id',
        element: (
          <ProtectedAgentPage>
            <ViewAgentPage />
          </ProtectedAgentPage>
        )
      },
      {
        path: '/studio/agents/edit/:id',
        element: (
          <ProtectedAgentPage>
            <EditAgentPage />
          </ProtectedAgentPage>
        )
      },

      // Playbooks Routes
      {
        path: '/studio/playbooks',
        element: <PlaybooksList />
      },
      {
        path: '/studio/interactive-playbooks',
        element: <InteractivePlaybooksList />
      },
      {
        path: '/studio/playbooks/add',
        element: <AddPlaybookPage />
      },
      {
        path: '/studio/interactive-playbooks/add',
        element: <AddDialoguePage />
      },
      {
        path: '/studio/playbooks/view/:id',
        element: (
          <ProtectedPlaybookPage>
            <ViewPlaybookPage />
          </ProtectedPlaybookPage>
        )
      },
      {
        path: '/studio/playbooks/edit/:id',
        element: (
          <ProtectedPlaybookPage>
            <EditPlaybookPage />
          </ProtectedPlaybookPage>
        )
      },

      // Executions Routes
      {
        path: '/studio/executions',
        element: <AllExecutionsPage />
      },
      {
        path: '/studio/executions/:executionId',
        element: <ExecutionDetailsPage />
      },

      // Prompts Routes
      {
        path: '/studio/prompts/edit/:id',
        element: <EditPromptPage />
      },
      {
        // Unknown path
        path: '*',
        element: <NotFound />
      }
    ]
  },

  // Admin routes with additional protection
  {
    element: <AdminProtectedLayout />,
    children: [
      {
        path: '/studio/admin',
        element: <AdminLanding />
      },
      {
        path: '/studio/admin/core-items',
        element: <CoreItemsPage />
      },
      {
        path: '/studio/admin/users',
        element: <UserManagementPage />
      },
      {
        path: '/studio/admin/adapters',
        element: <AdaptersList />
      },
      {
        path: '/studio/admin/adapters/add',
        element: <AddAdapterPage />
      },
      {
        path: '/studio/admin/adapters/edit/:id',
        element: <EditAdapterPage />
      },
      {
        path: '/studio/admin/adapters/test/:id',
        element: <TestAdapterPage />
      }
    ]
  },
  {
    // Unknown path
    path: '*',
    element: <NotFound />
  }
]);

function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <SessionExpiryHandler>
          <RouterProvider router={router} />
        </SessionExpiryHandler>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;